import { motion } from 'framer-motion';
import { Megaphone, Trophy, Users } from 'lucide-react';
import { formatIST } from '../lib/api';
import WinnersBlock from './WinnersBlock';
import SocialIcon from './SocialIcon';

export function StatusPill({ status }) {
  const map = {
    live: { label: 'LIVE', cls: 'bg-[#e8173a] text-white' },
    upcoming: { label: 'UPCOMING', cls: 'bg-white/10 text-[#c9c9c9]' },
    completed: { label: 'COMPLETED', cls: 'bg-white/5 text-[#8a8a8a]' },
  };
  const cfg = map[status] || map.upcoming;
  return (
    <span className={`text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

export function FreePill() {
  return (
    <span className="text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full border border-[#00c853]/60 text-[#00c853]">
      Free Entry
    </span>
  );
}

export function SlotBar({ joined, max }) {
  const pct = max > 0 ? Math.min(100, Math.round((joined / max) * 100)) : 0;
  return (
    <div>
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: `${pct}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="h-full rounded-full bg-[#e8173a]"
        />
      </div>
      <p className="mt-1.5 text-[11px] text-[#8a8a8a] font-medium flex items-center gap-1.5">
        <Users className="w-3 h-3" />
        {joined}/{max || '—'} slots filled
      </p>
    </div>
  );
}

function LinkButtons({ t }) {
  if (t.status === 'completed') return null;
  const links = [
    t.link_stream && { href: t.link_stream, label: 'Watch Stream', icon: 'youtube', testid: `link-stream-${t.id}` },
    t.link_whatsapp && { href: t.link_whatsapp, label: 'WhatsApp Group', icon: 'whatsapp', testid: `link-whatsapp-${t.id}` },
    t.link_organiser && { href: t.link_organiser, label: 'Organiser Channel', icon: 'megaphone', testid: `link-organiser-${t.id}` },
    t.link_discord && { href: t.link_discord, label: 'Discord', icon: 'discord', testid: `link-discord-${t.id}` },
  ].filter(Boolean);
  if (!links.length) return null;
  return (
    <div className="grid grid-cols-2 gap-2" data-testid={`tournament-links-${t.id}`}>
      {links.map((l) => (
        <a
          key={l.testid}
          data-testid={l.testid}
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-1 border border-[#e8173a]/50 text-[#e8173a] hover:bg-[#e8173a] hover:text-white text-[9px] sm:text-[10px] font-extrabold uppercase tracking-wide whitespace-nowrap px-1.5 py-2 rounded-lg transition-colors duration-200"
        >
          {l.icon === 'megaphone' ? (
            <Megaphone className="w-3.5 h-3.5" />
          ) : (
            <SocialIcon name={l.icon} className="w-3.5 h-3.5" />
          )}
          {l.label}
        </a>
      ))}
    </div>
  );
}

export default function TournamentCard({ t, index = 0 }) {
  const meta = [t.game_mode || t.game, t.map, formatIST(t.datetime)].filter(Boolean).join(' • ');
  const isFree = t.paid_type === 'free' || !t.entry_fee;

  return (
    <motion.article
      data-testid={`tournament-card-${t.id}`}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.6, delay: (index % 6) * 0.07, ease: [0.22, 1, 0.36, 1] }}
      className="card-surface card-surface-hover p-5 flex flex-col gap-4"
    >
      <div className="flex items-center gap-2">
        <StatusPill status={t.status} />
        {isFree && t.status !== 'completed' && <FreePill />}
      </div>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-white font-bold text-base leading-snug line-clamp-2">{t.title}</h3>
          {meta && <p className="mt-1 text-xs text-[#8a8a8a] line-clamp-2">{meta}</p>}
        </div>
        <div className="text-right shrink-0">
          <p className="text-[#e8173a] font-extrabold text-lg leading-none flex items-center gap-1.5 justify-end">
            <Trophy className="w-4 h-4" />
            ₹{Number(t.prize_pool || 0).toLocaleString('en-IN')}
          </p>
          <p className="mt-1 text-[11px] text-[#8a8a8a] uppercase tracking-wider">Prize Pool</p>
        </div>
      </div>
      <SlotBar joined={t.slots_joined} max={t.max_slots} />
      {t.status !== 'completed' && <LinkButtons t={t} />}
      {t.status === 'completed' && <WinnersBlock tournamentId={t.id} celebrate={index === 0} />}
    </motion.article>
  );
}
